/**
 * CommandPalette — Globale Suche (Cmd+K / Ctrl+K)
 *
 * Sucht über Seiten (clientseitig aus sidebar-config), Mitarbeiter,
 * Kunden und Cases (serverseitig via GET /api/search?q=).
 * Keyboard-Navigation: Pfeiltasten, Enter, Esc.
 *
 * @component
 * @since v2.279.0
 */
import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Search, X, Users, Building2, Headphones, FileText, Clock } from 'lucide-react';
import { SIDEBAR_GROUPS, SYSTEM_ITEMS, FORECAST_SUB_ITEMS, ABWESENHEITEN_SUB_ITEMS, LOHNVERRECHNUNG_SUB_ITEMS } from '../config/sidebar-config.jsx';

const API = import.meta.env.VITE_API_URL || '';
const RECENT_KEY = 'weship-cmd-recent';
const MAX_RECENT = 5;

function fuzzyMatch(text, query) {
  const t = text.toLowerCase();
  const q = query.toLowerCase();
  if (t.includes(q)) return true;
  let qi = 0;
  for (let i = 0; i < t.length && qi < q.length; i++) {
    if (t[i] === q[qi]) qi++;
  }
  return qi === q.length;
}

function getAllPages(t, permissions) {
  const pages = [];
  for (const group of SIDEBAR_GROUPS) {
    for (const item of group.items) {
      if (item.modul) {
        const p = permissions?.[item.modul];
        if (!p || p === 'none') continue;
      }
      if (item.path) {
        pages.push({ path: item.path, label: t(item.labelKey) });
      }
      if (item.subItems) {
        for (const sub of item.subItems) {
          pages.push({ path: sub.path, label: `${t(item.labelKey)} > ${t(sub.labelKey)}` });
        }
      }
    }
  }
  // Include system items (moved to Admin-Hub but still searchable)
  for (const item of SYSTEM_ITEMS) {
    if (item.modul) {
      const p = permissions?.[item.modul];
      if (!p || p === 'none') continue;
    }
    pages.push({ path: item.path, label: t(item.labelKey) });
  }
  // Include forecast sub-items (moved to tabs but still searchable)
  for (const item of FORECAST_SUB_ITEMS) {
    pages.push({ path: item.path, label: `${t('nav.forecast')} > ${t(item.labelKey)}` });
  }
  // Include abwesenheiten sub-items (moved to tabs but still searchable)
  for (const item of ABWESENHEITEN_SUB_ITEMS) {
    pages.push({ path: item.path, label: `${t('nav.abwesenheiten')} > ${t(item.labelKey)}` });
  }
  // Include lohnverrechnung sub-items (moved to tabs but still searchable)
  for (const item of LOHNVERRECHNUNG_SUB_ITEMS) {
    pages.push({ path: item.path, label: `${t('nav.lohnverrechnung')} > ${t(item.labelKey)}` });
  }
  return pages;
}

function loadRecent() {
  try {
    return JSON.parse(localStorage.getItem(RECENT_KEY) || '[]').slice(0, MAX_RECENT);
  } catch { return []; }
}

function saveRecent(item) {
  const list = loadRecent().filter(r => r.path !== item.path);
  list.unshift(item);
  localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, MAX_RECENT)));
}

export default function CommandPalette({ open, onClose }) {
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const [query, setQuery] = useState('');
  const [serverResults, setServerResults] = useState(null);
  const [activeIdx, setActiveIdx] = useState(0);
  const debounceRef = useRef(null);
  const listRef = useRef(null);

  const permissions = user?.permissions || {};
  const allPages = useMemo(() => getAllPages(t, permissions), [t, permissions]);

  // Server search with debounce
  useEffect(() => {
    if (!open) return;
    if (query.length < 2) { setServerResults(null); return; }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await fetch(`${API}/api/search?q=${encodeURIComponent(query)}&limit=5`, { credentials: 'include' });
        if (res.ok) setServerResults(await res.json());
      } catch { /* ignore */ }
    }, 250);
    return () => clearTimeout(debounceRef.current);
  }, [query, open]);

  // Focus input on open
  useEffect(() => {
    if (open) {
      setQuery('');
      setServerResults(null);
      setActiveIdx(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  // Build flat list of results
  const results = useMemo(() => {
    if (!query.trim()) {
      const recent = loadRecent();
      return recent.map(r => ({ ...r, group: 'recent' }));
    }

    const items = [];

    // Pages (client-side fuzzy)
    const pageMatches = allPages.filter(p => fuzzyMatch(p.label, query)).slice(0, 5);
    pageMatches.forEach(p => items.push({ group: 'pages', label: p.label, path: p.path, icon: 'page' }));

    // Server results
    if (serverResults) {
      (serverResults.mitarbeiter || []).forEach(m =>
        items.push({ group: 'mitarbeiter', label: m.name, sub: m.kostenstelle || '', path: `/mitarbeiter/${m.id}`, icon: 'ma', aktiv: m.aktiv })
      );
      (serverResults.kunden || []).forEach(k =>
        items.push({ group: 'kunden', label: k.name, sub: k.segment || '', path: `/kunden/${k.id}`, icon: 'kunde' })
      );
      (serverResults.cases || []).forEach(c =>
        items.push({ group: 'cases', label: c.name, sub: `#${c.id}`, path: `/cases/${c.id}`, icon: 'case' })
      );
    }
    return items;
  }, [query, allPages, serverResults]);

  // Reset activeIdx when results change
  useEffect(() => { setActiveIdx(0); }, [results.length]);

  const selectResult = useCallback((item) => {
    saveRecent({ label: item.label, path: item.path, icon: item.icon || 'page' });
    navigate(item.path);
    onClose();
  }, [navigate, onClose]);

  // Keyboard navigation
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') { onClose(); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx(i => Math.min(i + 1, results.length - 1)); return; }
    if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIdx(i => Math.max(i - 1, 0)); return; }
    if (e.key === 'Enter' && results[activeIdx]) { e.preventDefault(); selectResult(results[activeIdx]); }
  }, [results, activeIdx, selectResult, onClose]);

  // Scroll active item into view
  useEffect(() => {
    if (!listRef.current) return;
    const el = listRef.current.querySelector(`[data-idx="${activeIdx}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [activeIdx]);

  if (!open) return null;

  const groupLabels = {
    recent: t('cmdPalette.recent'),
    pages: t('cmdPalette.pages'),
    mitarbeiter: t('cmdPalette.employees'),
    kunden: t('cmdPalette.customers'),
    cases: t('cmdPalette.cases'),
  };

  const groupIcons = {
    page: <FileText size={16} className="text-[var(--text-tertiary)]" />,
    ma: <Users size={16} className="text-[var(--text-tertiary)]" />,
    kunde: <Building2 size={16} className="text-[var(--text-tertiary)]" />,
    case: <Headphones size={16} className="text-[var(--text-tertiary)]" />,
    recent: <Clock size={16} className="text-[var(--text-tertiary)]" />,
  };

  // Group results for rendering
  let lastGroup = null;
  let flatIdx = 0;

  return (
    <div className="fixed inset-0 z-[200] flex items-start justify-center pt-[15vh] bg-black/30" onClick={onClose}>
      <div
        className="bg-[var(--bg-card,#fff)] rounded-2xl border border-[var(--border-card,#E5E7EB)] shadow-xl w-full max-w-xl mx-4 overflow-hidden"
        onClick={e => e.stopPropagation()}
        onKeyDown={handleKeyDown}
      >
        {/* Search input */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-[var(--border-card,#E5E7EB)]">
          <Search size={18} className="text-[var(--text-tertiary)] flex-shrink-0" />
          <input
            ref={inputRef}
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder={t('cmdPalette.placeholder')}
            className="flex-1 bg-transparent text-sm text-[var(--text-primary)] placeholder-[var(--text-tertiary)] outline-none"
          />
          <kbd className="hidden sm:inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded border border-[var(--border-default,#E5E7EB)] text-[10px] text-[var(--text-muted)] font-mono">
            ESC
          </kbd>
          <button onClick={onClose} className="sm:hidden text-[var(--text-tertiary)] hover:text-[var(--text-primary)]">
            <X size={18} />
          </button>
        </div>

        {/* Results */}
        <div ref={listRef} className="max-h-[50vh] overflow-y-auto">
          {results.length === 0 && query.length >= 2 && (
            <div className="px-4 py-8 text-center text-sm text-[var(--text-tertiary)]">
              {t('cmdPalette.noResults')}
            </div>
          )}
          {results.length === 0 && query.length < 2 && query.length > 0 && (
            <div className="px-4 py-8 text-center text-sm text-[var(--text-tertiary)]">
              {t('cmdPalette.typeMore')}
            </div>
          )}
          {results.map((item, i) => {
            const showHeader = item.group !== lastGroup;
            lastGroup = item.group;
            const idx = flatIdx++;
            return (
              <div key={`${item.group}-${item.path}-${i}`}>
                {showHeader && (
                  <div className="px-4 pt-3 pb-1">
                    <span className="text-[11px] font-semibold uppercase tracking-wider text-[var(--text-muted)]">
                      {groupLabels[item.group] || item.group}
                    </span>
                  </div>
                )}
                <button
                  data-idx={idx}
                  onClick={() => selectResult(item)}
                  onMouseEnter={() => setActiveIdx(idx)}
                  className={`w-full text-left px-4 py-2.5 flex items-center gap-3 transition-colors ${
                    idx === activeIdx ? 'bg-[var(--bg-inset,#F8F9FC)]' : 'hover:bg-[var(--bg-inset,#F8F9FC)]'
                  }`}
                >
                  {groupIcons[item.icon] || groupIcons.page}
                  <div className="flex-1 min-w-0">
                    <span className={`text-sm text-[var(--text-primary)] ${item.aktiv === false ? 'opacity-50' : ''}`}>
                      {item.label}
                    </span>
                    {item.sub && (
                      <span className="text-xs text-[var(--text-tertiary)] ml-2">{item.sub}</span>
                    )}
                  </div>
                  {idx === activeIdx && (
                    <kbd className="hidden sm:inline-flex px-1.5 py-0.5 rounded border border-[var(--border-default,#E5E7EB)] text-[10px] text-[var(--text-muted)] font-mono">
                      Enter
                    </kbd>
                  )}
                </button>
              </div>
            );
          })}
        </div>

        {/* Footer hints */}
        <div className="px-4 py-2 border-t border-[var(--border-card,#E5E7EB)] flex items-center gap-4 text-[10px] text-[var(--text-muted)]">
          <span className="flex items-center gap-1"><kbd className="px-1 py-px rounded border border-[var(--border-default,#E5E7EB)] font-mono">↑↓</kbd> {t('cmdPalette.navigate')}</span>
          <span className="flex items-center gap-1"><kbd className="px-1 py-px rounded border border-[var(--border-default,#E5E7EB)] font-mono">↵</kbd> {t('cmdPalette.select')}</span>
          <span className="flex items-center gap-1"><kbd className="px-1 py-px rounded border border-[var(--border-default,#E5E7EB)] font-mono">esc</kbd> {t('cmdPalette.close')}</span>
        </div>
      </div>
    </div>
  );
}
